import React, { useState } from 'react';
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Alert, AlertDescription } from "../components/ui/alert";
import { 
  Send,
  ArrowLeft,
  User,
  MessageSquare,
  CheckCircle,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { api } from '../services/api';

interface SendPrayerState {
  content: string;
  name: string;
  isAnonymous: boolean;
  loading: boolean;
  error: string;
  success: boolean;
  charCount: number;
}

const MAX_LENGTH = 1000;

const SendPrayer: React.FC = () => {
  const [state, setState] = useState<SendPrayerState>({
    content: '',
    name: '',
    isAnonymous: false,
    loading: false,
    error: '',
    success: false,
    charCount: 0
  });

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_LENGTH) {
      setState(prev => ({
        ...prev,
        content: text,
        charCount: text.length
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!state.content.trim()) {
      setState(prev => ({ ...prev, error: 'Please enter your prayer request' }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: '' }));

    try {
      console.log('Sending prayer request:', {
        content: state.content.trim(),
        senderName: state.isAnonymous ? undefined : state.name.trim(),
        isAnonymous: state.isAnonymous
      });

      const response = await api.prayers.send({
        content: state.content.trim(),
        senderName: state.isAnonymous ? undefined : state.name.trim(),
        isAnonymous: state.isAnonymous
      });

      console.log('Prayer response:', response);

      setState(prev => ({ 
        ...prev, 
        success: true,
        content: '',
        name: '',
        charCount: 0
      }));

    } catch (error: any) {
      console.error('Prayer request error:', error);
      setState(prev => ({ 
        ...prev, 
        error: error.response?.data?.error || 'Failed to send prayer request. Please try again.' 
      }));
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
};

  if (state.success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6">
        <Card className="max-w-xl mx-auto">
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Prayer Request Sent
              </h2>
              <p className="text-gray-600">
                Your prayer request has been received. Others will be praying for you soon.
              </p>
            </div>
            <Button 
              onClick={() => window.history.back()}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Main Menu
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6">
      <Card className="max-w-xl mx-auto">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Send a Prayer Request
            </h1>
            <p className="text-gray-600">
              Share your prayer request and let others pray for you
            </p>
          </div>

          {state.error && (
            <Alert className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{state.error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                <MessageSquare className="w-4 h-4 mr-2" />
                Your Prayer Request
              </label>
              <div>
                <Textarea
                  value={state.content}
                  onChange={handleContentChange}
                  placeholder="Share your prayer request here..."
                  className="h-32 rounded-2xl resize-none"
                />
                <div className="mt-1 text-right text-sm text-gray-500">
                  {state.charCount}/{MAX_LENGTH}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                <User className="w-4 h-4 mr-2" />
                Your First Name (Optional)
              </label>
              <Input
                type="text"
                value={state.name}
                onChange={(e) => setState(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter your first name"
                className="rounded-xl"
                disabled={state.isAnonymous}
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="anonymous"
                checked={state.isAnonymous}
                onChange={(e) => setState(prev => ({ ...prev, isAnonymous: e.target.checked }))}
                className="rounded text-blue-500"
              />
              <label htmlFor="anonymous" className="text-sm text-gray-600">
                Send anonymously
              </label>
            </div>

            <div className="flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.history.back()}
                className="rounded-full flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                type="submit"
                disabled={state.loading || !state.content.trim()}
                className="rounded-full flex-1"
              >
                {state.loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {state.loading ? 'Sending...' : 'Send Prayer'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SendPrayer;